import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';

const WS_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('https://', 'wss://').replace('http://', 'ws://');
const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [feedEvents, setFeedEvents] = useState([]);
  const reconnectRef = useRef(null);
  const maxFeedEvents = 100;

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const ws = new WebSocket(`${WS_URL}/api/v1/gateway/ws`);

    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({
        action: 'subscribe',
        channels: ['feed:metrics', 'feed:all'],
      }));
      // Heartbeat
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ action: 'ping' }));
        }
      }, 25000);
      ws._pingInterval = pingInterval;
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.channel === 'feed:metrics') {
          setMetrics(msg.data);
        } else if (msg.channel === 'feed:all') {
          setFeedEvents(prev => {
            const updated = [msg.data, ...prev];
            return updated.slice(0, maxFeedEvents);
          });
        }
      } catch (e) {
        console.error('WS parse error:', e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      clearInterval(ws._pingInterval);
      reconnectRef.current = setTimeout(connect, 3000);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return (
    <WebSocketContext.Provider value={{ connected, metrics, feedEvents, ws: wsRef }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWS() {
  return useContext(WebSocketContext);
}
